import { addToast } from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Card } from 'reactstrap';
import DynamicForm from '../../../../../components/DynamicForm/DynamicForm';
import DymanicHealthCareCampus from '../../../../../components/DynamicForm/parts/DymanicHealthCareCampus';
import DynamicContact from '../../../../../components/DynamicForm/parts/DynamicContact';
import DynamicContactCampus from '../../../../../components/DynamicForm/parts/DynamicContactCampus';
import DynamicContactPDI from '../../../../../components/DynamicForm/parts/DynamicContactPDI';
import DynamicContactReformada from '../../../../../components/DynamicForm/parts/DynamicContactReformada';
import DynamicEducationalBackgroundCampus from '../../../../../components/DynamicForm/parts/DynamicEducationalBackgroundCampus';
import DynamicEducationalBackgroundPDI from '../../../../../components/DynamicForm/parts/DynamicEducationalBackgroundPDI';
import DynamicEducationalBackgroundReformada from '../../../../../components/DynamicForm/parts/DynamicEducationalBackgroundReformada';
import DynamicEmergencyContactPDI from '../../../../../components/DynamicForm/parts/DynamicEmergencyContactPDI';
import DynamicFamilyContactCampus from '../../../../../components/DynamicForm/parts/DynamicFamilyContactCampus';
import DynamicFamilyGroupDataReformada from '../../../../../components/DynamicForm/parts/DynamicFamilyGroupDataReformada';
import DynamicIcfesGradesReformada from '../../../../../components/DynamicForm/parts/DynamicIcfesGradesReformada';
import DynamicIcfesInfoReformada from '../../../../../components/DynamicForm/parts/DynamicIcfesReformada';
import { ButtonData } from '../../../../../components/DynamicForm/parts/RenderSections';
import { useParameters } from '../../../../../hooks/useParameters';
import useUserState from '../../../../../hooks/useUserState';
import { PermissionName } from '../../../../../types/Auth';
import { formatOptions } from '../../../../../utils/selectFormat';
import {
  childrensOptions,
  drivingLicenseOptions,
  educationalLevelOptions,
  familyGroupOptions,
  parentalSurvivalOptions,
  sexCIOptions,
  populationOptions,
  socioEconomicStratumOptions,
  SustainerType,
} from '../../../../../utils/selectOptions';
import StudentBackgroundNoSua from '../../../../AdmissionAndEnrollments/OnSiteEnrollment/TuitionProcessNoSua/steps/studentFile/parts/StudentBackgroundNoSua';
import StudentBackgroundNoSuaCampus from '../../../../AdmissionAndEnrollments/OnSiteEnrollment/TuitionProcessNoSua/steps/studentFile/parts/StudentBackgroundNoSuaCampus';
import StudentBackgroundNoSuaPDI from '../../../../AdmissionAndEnrollments/OnSiteEnrollment/TuitionProcessNoSua/steps/studentFile/parts/StudentBackgroundNoSuaPDI';
import StudentBackgroundNoSuaReformada from '../../../../AdmissionAndEnrollments/OnSiteEnrollment/TuitionProcessNoSua/steps/studentFile/parts/StudentBackgroundNoSuaReformada';
import { useAsyncOptionsFieldsLoader } from '../../../../StudentFileAsyncOptions/AsyncOptionsFieldsLoader';
import { savePostulant } from '../../../api/studentPersonalInfoRequests';

const DYNAMIC_FORM_SOURCE = 'admin-student-file';

const PostulantUpdateForm = ({
  postulantId,
  defaultValues,
  photo,
  prefix: rootPrefix,
  onError,
  showActions = true,
}: {
  postulantId: number;
  defaultValues: any;
  photo: string | null;
  prefix: string;
  onError: (code: string, onUnknown?: (() => void) | undefined) => void;
  showActions?: boolean;
}) => {
  //Custom Hooks
  const { t } = useTranslation();
  const history = useHistory();
  const {
    countryOptions,
    institutionTypeOptions,
    communeOptions,
    regionOptions,
    cityOptions,
  } = useParameters();
  const {
    data: {
      identificationTypes,
      healthInstitutions,
      genders,
      areaTypes,
      maritalStatus,
    } = {
      data: [],
    },
  } = useAsyncOptionsFieldsLoader();

  // memo
  const identificationTypeOptions = useMemo(() => {
    return formatOptions(identificationTypes ?? []);
  }, [identificationTypes]);

  const companyOptions = useMemo(() => {
    return formatOptions(healthInstitutions ?? []);
  }, [healthInstitutions]);

  const genderOptions = useMemo(() => {
    return formatOptions(genders ?? []);
  }, [genders]);

  const areaTypeOptions = useMemo(() => {
    return formatOptions(areaTypes ?? []);
  }, [areaTypes]);

  /**
   * Estado civil se toma del catálogo de la API (códigos: 'SG', 'MR', …), NO del array
   * hardcodeado `maritalStatusOptions` de utils/selectOptions.ts (ids numéricos 1..5).
   *
   * El código es la forma canónica en todo el sistema: es lo que escriben los portales
   * de matrícula, lo que guarda tp_step_form_response, y lo que `formatData` traduce a
   * `postulant.maritalStatusId` vía `maritalStatusConverter` ('SG' → 1). Además, la
   * hidratación (`formatReformada`) ya resolvía contra este catálogo, así que el array
   * numérico dejaba el campo vacío al recargar la ficha.
   */
  const maritalStatusOptions = useMemo(() => {
    return formatOptions(maritalStatus ?? []);
  }, [maritalStatus]);

  //Step Prefix
  const prefix = useMemo(() => `${rootPrefix}.studentFile`, [rootPrefix]);

  //Submit form function
  const onSubmit = useCallback(
    async (values: any) => {
      if (postulantId) {
        const responses = Object.entries(values)
          .map(([key, value]) => ({
            fieldName: key,
            value,
          }))
          .filter(({ value }) => value);

        /**
         * si el país es chile igual mando location y custom city
         */
        if (String(values.country?.label).toLowerCase().includes('chile')) {
          if (values.sustainerType?.value === SustainerType.LegalPerson) {
            responses.push(
              { fieldName: 'location', value: values.businessCommune?.label },
              {
                fieldName: 'customCity',
                value:
                  values.businesscustomCity ??
                  values.businessCity?.label ??
                  '-',
              },
            );
          } else {
            responses.push(
              { fieldName: 'location', value: values.commune?.label },
              {
                fieldName: 'customCity',
                value: values.customCity ?? values.city?.label ?? '-',
              },
            );
          }
        }

        const { data, error } = await savePostulant(postulantId, {
          responses,
          source: DYNAMIC_FORM_SOURCE,
        });

        if (data) {
          addToast({
            icon: 'success',
            color: 'success',
            text: 'Datos guardados correctamente',
          });
        } else if (error) {
          if (error.status === 400) {
            addToast({
              icon: 'error',
              color: 'danger',
              text: t('common.errors.emailDuplicate'),
            });
          } else {
            addToast({
              icon: 'error',
              color: 'danger',
              text: 'Ha ocurrido un error al guardar la información',
            });
          }
        }
      }
    },
    [postulantId, t],
  );

  const { isAuthorizedTo } = useUserState();
  const hasPermission = useMemo(
    () =>
      isAuthorizedTo(
        [PermissionName.STUDENT_MANAGEMENT_STUDENT_FILE_MANAGE],
        true,
      ) && showActions,
    [isAuthorizedTo, showActions],
  );

  const sectionComponents = (photo: string | null) => {
    return {
      postulantInformation: (
        <StudentBackgroundNoSua
          postulantId={postulantId}
          isStudentFileUpdate={true}
          photo={photo}
          key={Math.floor(Math.random() * 100)}
          readOnly={!hasPermission}
        />
      ),
      postulantInformationCampus: (
        <StudentBackgroundNoSuaCampus
          postulantId={postulantId}
          isStudentFileUpdate={true}
          photo={photo}
          key={Math.floor(Math.random() * 100)}
          readOnly={!hasPermission}
        />
      ),
      postulantInformationReformada: (
        <StudentBackgroundNoSuaReformada
          postulantId={postulantId}
          isStudentFileUpdate={true}
          photo={photo}
          key={Math.floor(Math.random() * 100)}
          readOnly={!hasPermission}
        />
      ),
      highschoolInformationReformada: (
        <DynamicEducationalBackgroundReformada
          type="studentFile"
          prefix={`tuitionProcess.onSite.studentFile.highschoolInformationReformada`}
          key={Math.floor(Math.random() * 100)}
          disabled={!hasPermission}
        />
      ),
      academicContactReformada: (
        <DynamicContactReformada
          type="studentFile"
          prefix={`tuitionProcess.onSite.studentFile.academicContactReformada`}
          key={Math.floor(Math.random() * 100)}
          disabled={!hasPermission}
        />
      ),
      familyGroupDataReformada: (
        <DynamicFamilyGroupDataReformada
          prefix={`tuitionProcess.onSite.studentFile.familyGroupDataReformada`}
          key={Math.floor(Math.random() * 100)}
          disabled={!hasPermission}
        />
      ),
      icfesInfoReformada: (
        <DynamicIcfesInfoReformada
          prefix={`tuitionProcess.onSite.studentFile.icfesInfoReformada`}
          key={Math.floor(Math.random() * 100)}
          disabled={!hasPermission}
        />
      ),
      icfesGradesReformada: (
        <DynamicIcfesGradesReformada
          prefix={`tuitionProcess.onSite.studentFile.icfesGradesReformada`}
          key={Math.floor(Math.random() * 100)}
          disabled={!hasPermission}
        />
      ),
      academicContact: (
        <DynamicContact
          type="studentFile"
          prefix={`tuitionProcess.onSite.studentFile.academicContactSection`}
          optionalFields={['addressExtra', 'phone']}
          key={Math.floor(Math.random() * 100)}
          disabled={!hasPermission}
        />
      ),
      academicContactCampus: (
        <DynamicContactCampus
          type="studentFile"
          prefix={`tuitionProcess.onSite.studentFile.academicContactSection`}
          key={Math.floor(Math.random() * 100)}
          hasEmail={true}
          disabled={!hasPermission}
        />
      ),
      healthCareCampus: (
        <DymanicHealthCareCampus
          type="studentFile"
          prefix={`tuitionProcess.onSite.studentFile.healtCareSection`}
          key={Math.floor(Math.random() * 100)}
          disabled={!hasPermission}
        />
      ),
      familyContactCampus: (
        <DynamicFamilyContactCampus
          type="sustainerFile"
          key={Math.floor(Math.random() * 100)}
          prefix={`${prefix}.familyGroupSection`}
          disabled={!hasPermission}
          hasEmail
        />
      ),
      highschoolInformationCampus: (
        <DynamicEducationalBackgroundCampus
          type="studentFile"
          prefix={`tuitionProcess.onSite.studentFile.highschoolInformationSection`}
          key={Math.floor(Math.random() * 100)}
          disabled={!hasPermission}
        />
      ),
      postulantInformationPDI: (
        <StudentBackgroundNoSuaPDI
          postulantId={postulantId}
          isStudentFileUpdate={true}
          photo={photo}
          key={Math.floor(Math.random() * 100)}
          readOnly={!hasPermission}
        />
      ),
      highschoolInformationPDI: (
        <DynamicEducationalBackgroundPDI
          type="studentFile"
          prefix={`tuitionProcess.onSite.studentFile.highschoolInformationSection`}
          key={Math.floor(Math.random() * 100)}
          disabled={!hasPermission}
        />
      ),
      academicContactPDI: (
        <DynamicContactPDI
          type="studentFile"
          prefix={`tuitionProcess.onSite.studentFile.academicContactSection`}
          key={Math.floor(Math.random() * 100)}
          disabled={!hasPermission}
        />
      ),
      emergencyContactPDI: (
        <DynamicEmergencyContactPDI
          type="studentFile"
          prefix={`tuitionProcess.onSite.studentFile.emergencyContactSection`}
          key={Math.floor(Math.random() * 100)}
          disabled={!hasPermission}
        />
      ),
    };
  };

  const formButtons: ButtonData[] = hasPermission
    ? [
        {
          type: 'button',
          text: t(`common.actions.cancel`),
          onClick: () => history.goBack(),
          disabledInSubmitting: true,
          fullwidth: true,
          outlined: true,
        },
        {
          type: 'submit',
          text: t(`common.actions.save`),
          disabledInLoading: true,
          disabledInSubmitting: true,
          fullwidth: true,
        },
      ]
    : [];

  return (
    <Card className="px-4 px-md-5 py-4">
      <DynamicForm
        key={1}
        source={DYNAMIC_FORM_SOURCE}
        dynamicFormId={1}
        defaultValues={defaultValues}
        sectionComponents={sectionComponents(photo)}
        options={{
          countryHighSchool: countryOptions,
          regionHighSchool: regionOptions,
          communeHighSchool: communeOptions,
          typeHighSchool: institutionTypeOptions,
          regionFamily: regionOptions,
          communeFamily: communeOptions,
          healthCareSystem: regionOptions,
          healthCareProvider: communeOptions,
          fatherEducationalLevel: educationalLevelOptions,
          motherEducationalLevel: educationalLevelOptions,
          familyGroup: familyGroupOptions,
          parentalSurvival: parentalSurvivalOptions,
          hasChildrens: childrensOptions,
          hasDrivingLicense: drivingLicenseOptions,
          maritalStatus: maritalStatusOptions,
          sexID: sexCIOptions,
          emergencyIdentificationType: identificationTypeOptions,
          // Reformada
          eps_institution: companyOptions,
          eps_contributorIdType: identificationTypeOptions,
          studentCharacterization_population: populationOptions,
          studentCharacterization_socioeconomicStratum: socioEconomicStratumOptions,
          // Dynamic form optionKey mappings
          identificationTypes: identificationTypeOptions,
          countries: countryOptions,
          genders: genderOptions,
          communes: communeOptions,
          regions: regionOptions,
          cities: cityOptions,
          areaTypes: areaTypeOptions,
        }}
        onSubmit={onSubmit}
        formButtons={formButtons}
        readOnly={!hasPermission}
        readOnlyFields={[
          'personalInformation_identificationType',
          'personalInformation_identification',
        ]}
      />
    </Card>
  );
};

export default PostulantUpdateForm;
